import { useState, useMemo } from 'react';
import { Box, Paper, Typography, Chip, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { knownDevices } from '../data/networkData';
import { mockSecurityData, hashStr } from './DeviceDetail';

// ── Generate mock alerts deterministically ─────────────────────

const ALERT_TEMPLATES = [
  { msg: (h) => `Successful login from 10.1.0.131 (JUMP-01)`,              sev: 'info'     },
  { msg: (h) => `AV definition updated — engine current`,                   sev: 'info'     },
  { msg: (h) => `STIG scan completed`,                                       sev: 'info'     },
  { msg: (h) => `Device checked in to ISE — Compliant`,                     sev: 'info'     },
  { msg: (h) => `Configuration backup completed`,                            sev: 'info'     },
  { msg: (h) => `Critical vulnerability detected — remote code execution`,   sev: 'critical' },
  { msg: (h) => `AV definition outdated — last updated 4+ days ago`,        sev: 'warning'  },
  { msg: (h) => `STIG compliance dropped below 70%`,                         sev: 'warning'  },
  { msg: (h) => `Failed login attempt from unknown host`,                    sev: 'warning'  },
  { msg: (h) => `Port scan detected from 10.1.0.50 (TENABLE-01)`,          sev: 'info'     },
  { msg: (h) => `Patch applied — system reboot scheduled`,                   sev: 'info'     },
  { msg: (h) => `ISE posture assessment failed`,                             sev: 'critical' },
  { msg: (h) => `Unauthorized outbound connection attempt blocked`,          sev: 'warning'  },
  { msg: (h) => `High vulnerability — privilege escalation (kernel driver)`, sev: 'warning'  },
];

const TIMES = [
  '08:12:04', '08:55:21', '09:14:47', '10:03:18', '11:22:05',
  '12:01:33', '13:45:09', '14:17:55', '15:06:42', '16:30:11',
];

function buildAlerts() {
  const hostnames = Array.from(new Set(Object.values(knownDevices).map(d => d.hostname)));
  const alerts = [];
  let id = 0;

  hostnames.forEach(hostname => {
    const h = hashStr(hostname);
    const count = 2 + (h % 3);
    for (let i = 0; i < count; i++) {
      const tIdx = (h + i * 7) % TIMES.length;
      const aIdx = (h + i * 13) % ALERT_TEMPLATES.length;
      const dayOffset = (h + i) % 3;
      alerts.push({
        id: id++,
        hostname,
        time: TIMES[tIdx],
        day: dayOffset === 0 ? 'Today' : dayOffset === 1 ? 'Yesterday' : '2 days ago',
        dayOrder: dayOffset,
        msg: ALERT_TEMPLATES[aIdx].msg(hostname),
        severity: ALERT_TEMPLATES[aIdx].sev,
      });
    }
  });

  return alerts.sort((a, b) => a.dayOrder - b.dayOrder || a.time.localeCompare(b.time));
}

const ALL_ALERTS = buildAlerts();
const ALL_HOSTNAMES = Array.from(new Set(ALL_ALERTS.map(a => a.hostname))).sort();

const SEV_COLOR = { critical: '#f44336', warning: '#ff9800', info: '#7a8fa6' };
const SEV_LABEL = { critical: 'Critical', warning: 'Warning', info: 'Info' };

function StatBox({ label, value, color }) {
  return (
    <Paper sx={{ flex: 1, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px 20px', textAlign: 'center' }}>
      <Typography sx={{ color: color || '#ffffff', fontSize: 36, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 0.5 }}>{label}</Typography>
    </Paper>
  );
}

function FilterChip({ label, active, color, onClick }) {
  return (
    <Chip label={label} size="small" onClick={onClick} sx={{
      backgroundColor: active ? (color ? `${color}33` : '#0d2137') : '#0a1525',
      color: active ? (color || '#00bcd4') : '#7a8fa6',
      border: `1px solid ${active ? (color || '#00bcd4') : '#1e3a5f'}`,
      fontWeight: active ? 700 : 400,
      cursor: 'pointer', fontSize: 12,
    }} />
  );
}

// ── ColFilter ──────────────────────────────────────────────────

function ColFilter({ label, options, value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const isFiltered = value.length > 0;
  const toggle = (val) => onChange(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography sx={{ color: isFiltered ? '#00bcd4' : '#7a8fa6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}{isFiltered ? ` (${value.length})` : ''}
      </Typography>
      {options.length > 0 && (
        <>
          <Box onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: isFiltered ? '#00bcd4' : '#1e3a5f', '&:hover': { color: '#00bcd4' } }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
          </Box>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
            PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', minWidth: 160 } }}>
            {isFiltered && (
              <MenuItem onClick={() => { onChange([]); setAnchor(null); }} sx={{ fontSize: 11, color: '#f44336', borderBottom: '1px solid #1e3a5f', '&:hover': { backgroundColor: '#0d2137' } }}>
                Clear filter
              </MenuItem>
            )}
            {options.map(opt => {
              const selected = value.includes(opt);
              return (
                <MenuItem key={opt} onClick={() => toggle(opt)}
                  sx={{ fontSize: 12, color: selected ? '#00bcd4' : '#c0cfe0', backgroundColor: selected ? '#0d2137' : 'transparent', '&:hover': { backgroundColor: '#0d2137' }, display: 'flex', justifyContent: 'space-between' }}>
                  {opt}
                  {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00bcd4' }} />}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
}

export default function Alerts() {
  // Sidebar filters
  const [filterSev, setFilterSev] = useState([]);
  const [filterDevice, setFilterDevice] = useState([]);
  const [timeRange, setTimeRange] = useState('7d');

  // Column-level filters
  const [filterSevCol, setFilterSevCol] = useState([]);
  const [filterDeviceCol, setFilterDeviceCol] = useState([]);
  const [filterDayCol, setFilterDayCol] = useState([]);

  const filtered = useMemo(() => ALL_ALERTS.filter(a => {
    if (filterSev.length > 0 && !filterSev.includes(a.severity)) return false;
    if (filterDevice.length > 0 && !filterDevice.includes(a.hostname)) return false;
    if (timeRange === '24h' && a.dayOrder > 0) return false;
    if (filterSevCol.length > 0 && !filterSevCol.includes(a.severity)) return false;
    if (filterDeviceCol.length > 0 && !filterDeviceCol.includes(a.hostname)) return false;
    if (filterDayCol.length > 0 && !filterDayCol.includes(a.day)) return false;
    return true;
  }), [filterSev, filterDevice, timeRange, filterSevCol, filterDeviceCol, filterDayCol]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(a => {
      if (!g[a.day]) g[a.day] = [];
      g[a.day].push(a);
    });
    return g;
  }, [filtered]);

  const toggleSev = (s) => setFilterSev(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const toggleDevice = (d) => setFilterDevice(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);

  const counts = {
    total:    ALL_ALERTS.length,
    critical: ALL_ALERTS.filter(a => a.severity === 'critical').length,
    warning:  ALL_ALERTS.filter(a => a.severity === 'warning').length,
    info:     ALL_ALERTS.filter(a => a.severity === 'info').length,
  };

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
        <StatBox label="Total Alerts" value={counts.total}    color="#ffffff" />
        <StatBox label="Critical"     value={counts.critical}  color="#f44336" />
        <StatBox label="Warning"      value={counts.warning}   color="#ff9800" />
        <StatBox label="Info"         value={counts.info}      color="#7a8fa6" />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

        {/* Sidebar */}
        <Paper sx={{ width: 260, flexShrink: 0, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px' }}>

          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1.25 }}>Time Range</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, marginBottom: 2.5 }}>
            {[['24h', 'Last 24h'], ['7d', '7 Days'], ['30d', '30 Days']].map(([val, lbl]) => (
              <FilterChip key={val} label={lbl} active={timeRange === val} onClick={() => setTimeRange(val)} />
            ))}
          </Box>

          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1.25 }}>Severity</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, marginBottom: 2.5 }}>
            {['critical', 'warning', 'info'].map(s => (
              <FilterChip key={s} label={SEV_LABEL[s]} active={filterSev.includes(s)} color={SEV_COLOR[s]} onClick={() => toggleSev(s)} />
            ))}
          </Box>

          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1.25 }}>Device</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {ALL_HOSTNAMES.map(h => (
              <FilterChip key={h} label={h} active={filterDevice.includes(h)} onClick={() => toggleDevice(h)} />
            ))}
          </Box>
        </Paper>

        {/* Alert Feed */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* Column filter header bar */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '90px 80px 160px 1fr 90px',
            padding: '8px 14px',
            marginBottom: 1,
            backgroundColor: '#0d1825',
            borderRadius: 1,
            border: '1px solid #1e3a5f',
          }}>
            <ColFilter label="Day"      options={['Today', 'Yesterday', '2 days ago']} value={filterDayCol}    onChange={setFilterDayCol} />
            <ColFilter label="Time"     options={[]}                                   value={[]}              onChange={() => {}} />
            <ColFilter label="Device"   options={ALL_HOSTNAMES}                        value={filterDeviceCol} onChange={setFilterDeviceCol} />
            <ColFilter label="Message"  options={[]}                                   value={[]}              onChange={() => {}} />
            <ColFilter label="Severity" options={['critical', 'warning', 'info']}      value={filterSevCol}    onChange={setFilterSevCol} />
          </Box>

          {Object.keys(grouped).length === 0 ? (
            <Typography sx={{ color: '#7a8fa6', padding: 3 }}>No alerts match the current filters.</Typography>
          ) : (
            ['Today', 'Yesterday', '2 days ago'].filter(day => grouped[day]).map(day => (
              <Box key={day} sx={{ marginBottom: 3 }}>
                <Typography sx={{ color: '#4a5a70', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 1, paddingLeft: 1 }}>{day}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {grouped[day].map(a => (
                    <Paper key={a.id} sx={{
                      backgroundColor: '#111827',
                      border: '1px solid #1e3a5f',
                      borderLeft: `3px solid ${SEV_COLOR[a.severity]}`,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      padding: '10px 14px',
                      '&:hover': { backgroundColor: '#0d1f33' },
                    }}>
                      <Typography sx={{ color: '#4a5a70', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>{a.time}</Typography>
                      <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>{a.hostname}</Typography>
                      <Typography sx={{ color: '#c0cfe0', fontSize: 13, flex: 1 }}>{a.msg}</Typography>
                      <Chip label={SEV_LABEL[a.severity]} size="small" sx={{
                        backgroundColor: `${SEV_COLOR[a.severity]}22`,
                        color: SEV_COLOR[a.severity],
                        fontWeight: 700, fontSize: 11, height: 20, flexShrink: 0,
                      }} />
                    </Paper>
                  ))}
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}
